/**
 * Navigatie-deeplinks naar de bestemming van een werkbon-locatie (FO-063).
 *
 * De Navigeer-knop in de portal toont een keuzemenu (Google Maps / Apple Kaarten
 * / Waze / adres kopiëren) zodat de monteur zijn eigen kaart-app kiest in plaats
 * van altijd naar Google Maps te gaan. Per app is er een pure URL-bouwer; de
 * geocode (`lat,lng`) wint van een vrij adres voor nauwkeurigheid ("coords >
 * adres"). Zonder bruikbare bestemming → `null` (de UI verbergt die optie).
 *
 * `placeId` is Google-specifiek en wordt door de andere bouwers genegeerd.
 */
export interface MapsBestemming {
  /** Vrij adres als bestemming wanneer er geen coords zijn. */
  adres?: string | null
  lat?: number | null
  lng?: number | null
  placeId?: string | null
}

/**
 * Eén bron voor de "coords > adres"-precedentie: coords (nauwkeurigst) winnen
 * van het vrije adres; zonder bruikbare van beide → `null`.
 */
type Bestemming =
  | { soort: 'coords'; lat: number; lng: number }
  | { soort: 'adres'; tekst: string }

function resolveBestemming({ adres, lat, lng }: MapsBestemming): Bestemming | null {
  if (lat != null && lng != null) return { soort: 'coords', lat, lng }
  const tekst = adres?.trim()
  if (tekst) return { soort: 'adres', tekst }
  return null
}

export function buildMapsHref(bestemming: MapsBestemming): string | null {
  const b = resolveBestemming(bestemming)
  if (!b) return null
  const dest = b.soort === 'coords' ? `${b.lat},${b.lng}` : encodeURIComponent(b.tekst)
  const url = `https://www.google.com/maps/dir/?api=1&destination=${dest}`
  return bestemming.placeId
    ? `${url}&destination_place_id=${encodeURIComponent(bestemming.placeId)}`
    : url
}

export function buildAppleMapsHref(bestemming: MapsBestemming): string | null {
  const b = resolveBestemming(bestemming)
  if (!b) return null
  const dest = b.soort === 'coords' ? `${b.lat},${b.lng}` : encodeURIComponent(b.tekst)
  return `https://maps.apple.com/?daddr=${dest}`
}

export function buildWazeHref(bestemming: MapsBestemming): string | null {
  const b = resolveBestemming(bestemming)
  if (!b) return null
  return b.soort === 'coords'
    ? `https://waze.com/ul?ll=${b.lat},${b.lng}&navigate=yes`
    : `https://waze.com/ul?q=${encodeURIComponent(b.tekst)}&navigate=yes`
}

/**
 * Apple-toestel (iPhone/iPad/Mac) op basis van de user-agent — bepaalt of de
 * "Apple Kaarten"-optie zin heeft. iPad op iPadOS 13+ meldt zich als
 * "Macintosh"; dat is óók een Apple-toestel, dus de match klopt.
 */
export function isAppleUserAgent(ua: string): boolean {
  return /iphone|ipad|ipod|macintosh|mac os x/i.test(ua)
}
